import { createReducer, on } from '@ngrx/store';
import { Departures } from 'app/core/enums/common.enum';
import { UiItem } from 'app/core/interfaces/ui';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as WindowSalesActions from './window-sales.actions';
import { WindowsSale } from './window-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface WindowsSalesState {
    sales: UiItem<WindowsSale>[];
    sale: UiItem<WindowsSale> | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialWindowSalesState: WindowsSalesState = {
    sales: [],
    sale: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const windowSalesReducer = createReducer(
    initialWindowSalesState,

    // On Load Window Sales
    on(WindowSalesActions.loadWindowsSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WindowSalesActions.loadWindowsSalesSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: data,
    })),
    on(WindowSalesActions.loadWindowsSalesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear
    on(WindowSalesActions.clearWindowsSales, (state) => ({
        ...state,
        sales: [],
        sale: null,
        isLoading: false,
        error: null,
    })),

    // On Create
    on(WindowSalesActions.createWindowsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WindowSalesActions.createWindowsSaleSuccess, (state, { sale }) => ({
        ...state,
        isLoading: false,
        sales: ((): UiItem<WindowsSale>[] => {
            const sales = state.sales || [];
            let updatedSales = [];
            sales.some((s) => s.location.id === sale.location.id)
                ? (updatedSales = sales.map((_sale: UiItem<WindowsSale>) =>
                      _sale.location.id === sale.location.id
                          ? {
                                ..._sale,
                                children: [..._sale.children, ...sale.children],
                                totals: sale.totals,
                            }
                          : _sale
                  ))
                : (updatedSales = [...sales, sale]);
            return updatedSales;
        })(),
    })),
    on(WindowSalesActions.createWindowsSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(WindowSalesActions.updateWindowsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WindowSalesActions.updateWindowsSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: state.sales.map((s) =>
            s.location.id === data.location.id
                ? {
                      ...s,
                      children: s.children.map((child) =>
                          child.saleId === data.children[0].saleId
                              ? data.children[0]
                              : child
                      ),
                      totalsLocal: data.totalsLocal,
                      totals: data.totals,
                  }
                : s
        ),
    })),
    on(WindowSalesActions.updateWindowsSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update Location Gross
    on(
        TotalGrossActions.updateTotalGrossSuccess,
        (state, { data, departure }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((s) =>
                s.location.id === data.location_id &&
                departure === Departures.MIDWAY
                    ? {
                          ...s,
                          cash: data.cash,
                          credit_card: data.credit_card,
                          gross: data.total_gross,
                          total_money: data.total_gross,
                          totalsLocal: {
                              cash: data.total_local.cash,
                              credit_card: data.total_local.credit_card,
                              count: s.totalsLocal?.count,
                              amount: data.total_gross,
                          },
                      }
                    : s
            ),
        })
    ),

    // On Delete
    on(WindowSalesActions.deleteWindowsSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        WindowSalesActions.deleteWindowsSaleSuccess,
        (state, { deletedSales, remainingSales }) => ({
            ...state,
            isLoading: false,
            sales: (() => {
                if (remainingSales.length === 0) {
                    return state.sales.filter(
                        (sale: UiItem<WindowsSale>) =>
                            !sale.children.some(
                                (child) => child.saleId === deletedSales[0]
                            )
                    );
                } else {
                    console.warn(
                        'Remaining sales after deletion:',
                        remainingSales
                    );
                    return state.sales;
                }
            })(),
        })
    ),
    on(WindowSalesActions.deleteWindowsSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sale Item
    on(WindowSalesActions.deleteWindowsSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        WindowSalesActions.deleteWindowsSaleItemSuccess,
        (state, { deletedSaleItem, totals, totalsLocal }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((_sale: UiItem<WindowsSale>) =>
                _sale.children.some((child) => child.saleId === deletedSaleItem)
                    ? {
                          ..._sale,

                          children: _sale.children.filter(
                              (child) => child.saleId !== deletedSaleItem
                          ),
                          cash: totalsLocal.cash,
                          credit_card: totalsLocal.credit_card,
                          gross: totalsLocal.amount,
                          totals: totals,
                          totalsLocal,
                      }
                    : _sale
            ),
        })
    ),
    on(WindowSalesActions.deleteWindowsSaleItemFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    }))
);
